import { useState, useEffect, type FC, useMemo, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import Provision from '../../api/provision';
import Table from '../../components/Table';
import rbacCheck from '../../rbac/rbacCheck';

import CreateContactFormModal from './CreateContactFormModal';

import type { ErrorType } from '@/types';
import type { Contact } from '@/types/contactTypes';
import type { JuiceGroupContactsProps } from '@/types/juicegroupTypes';
import type { RoleKey } from '@/types/rules';
import type { TableRowData } from '@/types/tableTypes';

const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

const escapeCsvValue = (value: string | undefined): string => {
  const stringValue = value ?? '';
  // Prefix values that a spreadsheet app would otherwise interpret as a formula
  // (CSV formula injection) with a single quote so they're read back as plain text.
  const sanitized = CSV_FORMULA_PREFIX.test(stringValue) ? `'${stringValue}` : stringValue;
  return /[",\n\r]/.test(sanitized) ? `"${sanitized.replace(/"/g, '""')}"` : sanitized;
};

const JuiceGroupContacts: FC<JuiceGroupContactsProps> = ({
  juiceGroup: propJuiceGroup = null,
  allowSelection = true,
}) => {
  const { juiceGroup: paramJuiceGroup } = useParams();
  const juiceGroup: string | null = propJuiceGroup ?? (paramJuiceGroup as string | null) ?? null;
  const intl = useIntl();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const roles = useSelector((state: { user: { roles: string[] } }) => state.user.roles);

  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType | null>(null);

  const canAddEditContacts = rbacCheck(roles as RoleKey[], '/products:CONTACTS_PATCH');

  useEffect(() => {
    const getContacts = async (): Promise<void> => {
      try {
        setError(null);
        const response = await Provision.fetchContacts({ juiceGroup });

        if (Array.isArray(response.data)) {
          setContacts(response.data);
        }
      } catch (err) {
        setError(err as ErrorType);
      }
    };

    getContacts();
  }, [juiceGroup]);

  const lookupType: Record<Contact['type'], string> = useMemo(
    () => ({
      stakeHolder: 'Stakeholder',
      serviceOwner: 'Service Owner',
      tenantOwner: 'Tenant Owner',
      onboardingSupport: 'Onboarding Support',
    }),
    []
  );

  const fields = useMemo(
    () => [
      { id: 'email', title: { id: 'email' } },
      { id: 'role', title: { id: 'role' } },
      { id: 'name', title: { id: 'name' } },
      { id: 'phone', title: { id: 'phone' } },
      { id: 'slack', title: { id: 'slack' } },
    ],
    []
  );

  const rows = useMemo(
    () =>
      contacts?.map(contact => ({
        key: contact._id,
        ...(allowSelection && {
          onRowClick: () => navigateRef.current(`contacts/${contact._id}`),
        }),
        data: [
          {
            field: 'email',
            value: contact.email,
          },
          {
            field: 'role',
            value: lookupType[contact.type],
          },
          {
            field: 'name',
            value: contact.name,
          },
          {
            field: 'phone',
            value: contact.phoneNumber,
          },
          {
            field: 'slack',
            value: contact.slackChannel,
          },
        ],
      })) ?? [],
    [allowSelection, contacts, lookupType]
  );

  const contactsFile = useMemo(() => {
    const header = ['Name', 'Email', 'Role', 'Phone', 'Slack'];
    const csvRows =
      contacts?.map(contact =>
        [
          contact.name,
          contact.email,
          lookupType[contact.type],
          contact.phoneNumber,
          contact.slackChannel,
        ]
          .map(escapeCsvValue)
          .join(',')
      ) ?? [];
    const csvContent = [header.join(','), ...csvRows].join('\n');

    return new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
  }, [contacts, lookupType]);

  const handleCreateSubmit = useCallback((newContact: Contact) => {
    setContacts(prev => [...(prev ?? []), newContact]);
    setCreateModalOpened(false);
  }, []);

  const handleCreateCancel = useCallback(() => {
    setCreateModalOpened(false);
  }, []);

  const handleCreateClick = useCallback(() => {
    setCreateModalOpened(true);
  }, []);

  const createAction = useMemo(
    () => ({
      disabled: !canAddEditContacts,
      onClick: handleCreateClick,
      tooltip: !canAddEditContacts ? intl.formatMessage({ id: 'contactCreateDisabled' }) : '',
      label: intl.formatMessage({ id: 'create' }),
    }),
    [canAddEditContacts, handleCreateClick, intl]
  );

  const exportData = useMemo(
    () =>
      juiceGroup
        ? {
            name: intl.formatMessage({ id: 'export' }),
            fileName: `${juiceGroup}_contacts.csv`,
            file: contactsFile,
            tooltip: '',
          }
        : null,
    [juiceGroup, contactsFile, intl]
  );

  return (
    <>
      {createModalOpened && (
        <CreateContactFormModal
          juiceGroup={juiceGroup ?? ''}
          contacts={contacts ?? []}
          onSubmit={handleCreateSubmit}
          onCancel={handleCreateCancel}
        />
      )}
      <Table
        header=''
        fields={fields}
        rows={rows as TableRowData[]}
        error={error}
        createAction={createAction}
        exportData={exportData}
        searchParamKey='juiceGroupContactsSearch'
      />
    </>
  );
};

export default JuiceGroupContacts;
